import React, { useState, useEffect, useContext } from 'react';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";
import RenderRichText from '../../utils/RenderRichText';
import API from '../../api';
import { shuffleArray, calcRowBasis, scrollToID } from '../../utils/Misc';
import Settings from '../../constants/Settings';
import { CenterModal, ModalTitle, ModalCloseTarget } from 'react-spring-modal';
import FadeInInductee from '../utility/FadeInInductee';
import YoutubeEmbed from './YoutubeEmbed';
import Analytics from '../../utils/Analytics';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import hof_logo from '../../assets/images/HOF-LOGO-transparent-white.png';
import hof_logo_2x from '../../assets/images/HOF-LOGO-transparent-white@2x.png';
import card_bg from '../../assets/images/inductee-card-bg.jpg';
import InducteeModal from '../InducteeModal';
import {
  Box,
  Button,
  Image,
  Layer,
  Markdown,
  ResponsiveContext,
  Text
} from 'grommet'

const ComponentInductees = (props) => {
  const { field, match, t, i18n } = props;
  const size = React.useContext(ResponsiveContext);
  const [categories, setCategories] = useState([]);
  const [popup, setPopup] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);

  // Need to work what year we are using
  var YEAR = "inductee2023"
  if (field.hasOwnProperty("fields") && field.fields){
    if (field.fields.hasOwnProperty("year")){
      YEAR = "inductee" + field.fields.year
    }
  }

  const loadCategories = () => {
    // Get Categories
    API.getCategories(i18n).then( (res) => {
      // Work out the winner to show
      const year = parseInt((field.fields.year ? field.fields.year : Settings.CURRENT_HOF_YEAR));
      res.items.forEach( (category) => {
        const winners = category.fields.inductees.filter( (winner) => {
          return parseInt(winner.fields.year) === year;
        });
        if (winners.length > 0){
          category.winner = winners[0].fields.product;
        } else {
          category.winner = false;
        }
      });
      
      setCategories(res.items)
    })
    .catch(console.error)
  }

  const openPopup = (item) => {
    Analytics.TrackPageView(item.fields.name, "/inductee/" + _.kebabCase(item.fields.name));
    setPopup(item);
    setPopupOpen(true);
  }

  const closePopup = (item) => {
    setPopupOpen(false);
    setTimeout(() => { setPopup(item)}, 500)
  }

  // Similar to componentDidMount and componentDidUpdate:
  // Empty array as second param so ocde only runs once
  // https://css-tricks.com/run-useeffect-only-once/
  useEffect(() => {
    loadCategories();
  }, []);

  function renderTitle() {
    if (field.fields.hasOwnProperty('title') && field.fields.title){
      return (
        <h2>
          {field.fields.title}
        </h2>
      )
    } else {
      return null
    }
  }

  function renderText() {
    if (field.fields.hasOwnProperty('text') && field.fields.text){
      return (
        <Text className={"richText " + checkBig() ? 'bigText' : 'normalText'} textAlign={checkCentered()} >
        {RenderRichText(field.fields.text)}
        </Text>
      )
    } else {
      return null
    }
  }

  function checkBig() {
    if (field.fields.hasOwnProperty('big') && field.fields.big){
      return "xlarge"
    } else {
      return "medium"
    }
  }

  function checkCentered() {
    if (field.fields.hasOwnProperty('centered') && field.fields.centered){
      return "center"
    } else {
      return "start"
    }
  }

  function checkCenteredPadding() {
    if (field.fields.hasOwnProperty('centered') && field.fields.centered){
      return "large"
    } else {
      return "none"
    }
  }

  const renderCategoryImage = (category, float) => {
    if (
      category.fields.hasOwnProperty('imageSmall')
      && category.fields.image
    ){
      return (
        <img
          className={float ? "categoryImage categoryImageFloat" : "categoryImage"}
          //fit="contain"
          //fill={false}
          src={category.fields.imageSmall.fields.file.url}
        />
      )
    } else {
      return (
        <p>No category image</p>
      )
    }
  }

  const renderInducteeImage = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('image')
    ){
      return (
        <img
          className="inducteeImage"
          src={category.winner.fields.image.fields.file.url}
        />
      )
    } else {
      return (
        <p>No inductee image</p>
      )
    }
  }

  const renderInducteeName = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('name')
    ){
      return (
        <h3>{category.winner.fields.name}</h3>
      )
    } else {
      return null;
    }
  }

  const renderInducteeSpecs = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('specs')
    ){
      return (
        <Box
          className="specs"
          direction="row"
          justify="stretch"
        >
          { category.winner.fields.specs.map( (spec, index) => {
            return (
              <Box className="spec" flex="grow" key={index}>
                <h4 className="specTitle">{spec.fields.title}</h4>
                <div className="specValue">{spec.fields.value}</div>
              </Box>
            )
          })}
        </Box>
      )
    } else {
      return null
    }
  }

  const renderInducteeShortDescription = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('shortDescription')
    ){
      return (
        <p className="shortDescription">{category.winner.fields.shortDescription}</p>
      )
    } else {
      return null
    }
  }

  const renderInducteeLongDescription = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('longDescription')
    ){
      return (
        <Box className="longDescription">
          <Markdown
            components={{
              "p": {
                "component": "Paragraph",
                "props": {"size": "medium"}
              }
            }}
          >{category.winner.fields.longDescription }</Markdown>
        </Box>
      )
    } else {
      return null
    }
  }

  const renderInducteeVideo = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('videoId')
      && category.winner.fields.videoId
    ){
      return (
        <Box className="video">
          <YoutubeEmbed embedId={category.winner.fields.videoId} />
        </Box>
      )
    } else {
      return null
    }
  }

  const renderInducteeCTA = (category) => {
    if (
      category.hasOwnProperty("winner")
      && category.winner
      && category.winner.fields.hasOwnProperty('cta01')
      && category.winner.fields.hasOwnProperty('ctaLink')
      && category.winner.fields.cta01
      && category.winner.fields.ctaLink
    ){
      return (
        <Box className="ctaWrapper" pad="medium" justify="center" align="center">
          <Button
            primary
            href={category.winner.fields.ctaLink}
            label={category.winner.fields.cta01}
          />
        </Box>
      )
    } else {
      return null
    }
  }

  const renderPopup = () => {
    if (!popup){
      return null
    } else {
      return (
        <Box className="popupInductee">
          <Box flex={{grow: 0,shrink: 1}} className="header">
            {renderCategoryImage(popup)}
            {renderInducteeImage(popup)}
            <div className="inducteeLabel">{t('inductee')}</div>
            {renderInducteeName(popup)}
          </Box>
          <Box flex={{grow: 1, shrink: 0}} className="body">
            {renderInducteeShortDescription(popup)}
            {renderInducteeSpecs(popup)}
            {renderInducteeCTA(popup)}
            {renderInducteeVideo(popup)}
            {renderInducteeLongDescription(popup)}
          </Box>
        </Box>
      )
    }
  }

  const calcBasis = () => {
    return calcRowBasis(size)
  }

  return (
      <Box className="inductees" id="inductees" pad="large">
      {/**<Text>grommet size: {size} / basis: {calcBasis()}</Text>**/}
        <Box className="header">
          {renderTitle()}
          {renderText()}
        </Box>

        <CenterModal
          isOpen={popupOpen}
          onDismiss={() => closePopup(false)}
          contentTransition={{
            from: { background: 'black', transform: 'translateY(-100%)' },
            enter: { background: 'black', transform: 'translateY(0)' },
            leave: { background: 'black', transform: 'translateY(-100%)' }
          }}
          //contentProps={{ style: staticModalStyles }}
        >
          <ModalCloseTarget>
            <button className="modalClose"><FontAwesomeIcon icon={faTimesCircle} /></button>
          </ModalCloseTarget>
            {renderPopup()}
        </CenterModal>

        <Box
          direction="row"
          wrap={true}
          //gap="medium"
          className="inducteesContainer"
        >

          { categories.map( (category, index) => {
            return (
              <FadeInInductee
                key={index}
                delay={index}
                calcBasis={calcBasis}
                onClick={() => {openPopup(category)}}
              >
                {renderCategoryImage(category, true)}
                {renderInducteeImage(category)}
                <Box className="textWrapper inducteeContent">
                  {renderInducteeName(category)}
                  {renderInducteeShortDescription(category)}
                <Box className="buttonWrapper inducteeButtonContainer">
                  <Button
                    primary
                    label={t("Learn more")}
                    fill="horizontal"
                    focusIndicator={false}
                    hoverIndicator={false}
                  />
                </Box>
                </Box>

              </FadeInInductee>
            )
          })}

              <FadeInInductee
                calcBasis={calcBasis}
                background="#FF18BD"
                key={5}
                delay={5}
              >

                <Image
                  className="categoryImage"
                  fit="contain"
                  fill={false}
                  src={hof_logo}
                  srcSet={`${hof_logo} 1x, ${hof_logo_2x} 2x, `}
                  style={{flexGrow: 1, flexBasis: "75%"}}
                />

                <h3 className="bannerHeading">{t("View the Museum")}</h3>

                <Box
                  className="buttonWrapper inducteeButtonContainer"
                  flex={false}
                >
                    <Link
                      to={`/${i18n.language}/museum`}
                      style={{display: "block", textAlign: "center"}}
                    >
                      <Button className="darkButton" primary label={t("Learn more")} fill="horizontal" hoverIndicator={false} focusIndicator={false} />
                    </Link>
                </Box>
                </FadeInInductee>

        </Box>
      </Box>
  )
}

export default ComponentInductees;
